import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Key as KeyIcon,
  AccountCircle as AccountIcon
} from '@mui/icons-material';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';
import MFASetup from '../components/auth/MFASetup';

// TOTP Input Component (same as LoginPage)
const TOTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);

  useEffect(() => {
    const digitsArray = value.split('').slice(0, 6);
    while (digitsArray.length < 6) {
      digitsArray.push('');
    }
    setDigits(digitsArray);
  }, [value]);

  const handleChange = (index, newValue) => {
    if (newValue.length > 1) {
      newValue = newValue.slice(-1);
    }
    
    if (!/^\d*$/.test(newValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);
    
    const totpValue = newDigits.join('');
    onChange(totpValue);

    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedDigits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (pastedDigits.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedDigits.length && i < 6; i++) {
        newDigits[i] = pastedDigits[i];
      }
      setDigits(newDigits);
      onChange(pastedDigits);
      
      const nextIndex = Math.min(pastedDigits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 600,
              fontFamily: 'monospace',
              color: 'white',
              padding: '12px 0',
            }
          }}
          sx={{
            width: 56,
            '& .MuiOutlinedInput-root': {
              background: 'rgba(52, 152, 219, 0.05)',
              borderRadius: 2,
              '& fieldset': {
                borderColor: 'rgba(52, 152, 219, 0.3)',
                borderWidth: 2,
              },
              '&:hover fieldset': {
                borderColor: 'rgba(52, 152, 219, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3498db',
                borderWidth: 2,
              },
              '&.Mui-disabled fieldset': {
                borderColor: 'rgba(52, 152, 219, 0.2)',
              },
            },
            '& .MuiInputBase-input': {
              color: 'white',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        />
      ))}
    </Box>
  );
};

const CustomerLogin = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { getCustomer, customerLogin } = useApi();
  
  const [customer, setCustomer] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    totpCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginStep, setLoginStep] = useState('password'); // 'password', 'mfa', 'setup'
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);

  useEffect(() => {
    loadCustomer();
  }, [customerId]);

  const loadCustomer = async () => {
    try {
      const customerData = await getCustomer(customerId);
      setCustomer(customerData);
      
      // Set default username to admin@customername
      setFormData(prev => ({
        ...prev,
        username: `admin@${customerData.name}`
      }));
    } catch (err) {
      console.error('Failed to load customer:', err);
      navigate('/customers');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await customerLogin(
        customerId,
        formData.username,
        formData.password,
        formData.totpCode
      );
      
      // Add debug logging
      console.log('Login response received:', response);
      console.log('Response data:', response.data);
      
      if (response.success && response.data) {
        const loginData = response.data;
        
        // Check for MFA setup requirement FIRST (before checking token or MFA code)
        if (loginData.requiresMfaSetup) {
          // MFA setup is required - use the loginData directly
          console.log('MFA setup required, setting up data:', loginData);
          setMfaSetupData(loginData);
          setLoginStep('setup');
          setMfaRequired(false); // Ensure MFA code entry is not shown
          toast.info('MFA setup is mandatory for customer admin');
        } else if (loginData.token) {
          // Check if we have a token (indicates successful login)
          // Login completed successfully
          localStorage.setItem('customerToken', loginData.token);
          localStorage.setItem('customerId', customerId);
          toast.success('Login successful!');
          navigate(`/customers/${customerId}/dashboard`);
        } else if (loginData.requiresMFA && !loginData.requiresMfaSetup) {
          // MFA code is required but setup is already done
          setMfaRequired(true);
          setLoginStep('mfa');
          toast.success('Password verified. Please enter your MFA code.');
        } else {
          console.log('Unexpected login response structure:', loginData);
          toast.error('Unexpected login response');
        }
      } else {
        console.log('Login response missing success or data:', response);
        toast.error('Invalid login response');
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error('Login failed: ' + err.message);
      // Reset MFA state on error
      setMfaRequired(false);
      setLoginStep('password');
      setFormData(prev => ({ ...prev, totpCode: '' }));
    } finally {
      setLoading(false);
    }
  };

  const handleMFASetupComplete = async () => {
    toast.success('MFA setup completed successfully. You can now login.');
    setMfaSetupData(null);
    setLoginStep('password');
    setFormData({ username: `admin@${customer.name}`, password: '', totpCode: '' });
  };

  const resetToPasswordStep = () => {
    setLoginStep('password');
    setMfaRequired(false);
    setFormData(prev => ({ ...prev, totpCode: '' }));
  };

  if (!customer) {
    return <div>Loading...</div>;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, rgba(13, 17, 23, 0.98), rgba(22, 27, 34, 0.95))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
        position: 'relative',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={0}
          sx={{
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '3px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 2,
            p: 4,
            width: 480,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                background: 'transparent',
                mx: 'auto',
                mb: 2,
              }}
            >
              <img 
                src="/ck-auth-logo.png" 
                alt="CK Prism"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Avatar>
            
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 700,
                fontSize: '1.8rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
                mb: 1,
              }}
            >
              {customer.name}
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '0.95rem',
              }}
            >
              Customer Admin Login
            </Typography>
          </Box>

          <AnimatePresence mode="wait">
            {/* MFA Setup Flow */}
            {loginStep === 'setup' && mfaSetupData && (
              <motion.div
                key="mfa-setup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <MFASetup 
                  setupData={mfaSetupData}
                  onComplete={handleMFASetupComplete}
                  onError={(error) => toast.error(error)}
                />
              </motion.div>
            )}

            {/* Login Form */}
            {loginStep !== 'setup' && (
              <motion.div
                key="login-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    disabled={loading || mfaRequired}
                    InputProps={{ 
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                        '&.Mui-focused': {
                          color: '#667eea',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    required
                    disabled={loading || mfaRequired}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                        '&.Mui-focused': {
                          color: '#667eea',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <KeyIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                            disabled={mfaRequired}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* MFA Step */}
                  <AnimatePresence>
                    {mfaRequired && loginStep === 'mfa' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: 'white',
                              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                              fontWeight: 600,
                              textAlign: 'center',
                              mb: 2,
                            }}
                          >
                            Enter TOTP Code
                          </Typography>
                          
                          <TOTPInput
                            value={formData.totpCode}
                            onChange={(value) => setFormData(prev => ({ ...prev, totpCode: value }))}
                            disabled={loading}
                          />

                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.5)',
                              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                              fontSize: '0.75rem',
                              display: 'block',
                              textAlign: 'center',
                              mt: 2,
                            }}
                          >
                            Enter the 6-digit code from your authenticator app
                          </Typography>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Box sx={{ display: 'flex', gap: 2, flexDirection: mfaRequired ? 'row' : 'column' }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading || (!formData.password || (mfaRequired && formData.totpCode.length !== 6))}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 2,
                        py: 1.5,
                        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          background: 'rgba(255, 255, 255, 0.12)',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                    >
                      {loading ? 'Logging in...' : (mfaRequired ? 'Complete Login' : 'Login')}
                    </Button>
                    
                    {mfaRequired && (
                      <Button
                        variant="outlined"
                        onClick={resetToPasswordStep}
                        disabled={loading}
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          color: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: 2,
                          py: 1.5,
                          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                          fontWeight: 600,
                          textTransform: 'none',
                          minWidth: '100px',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            background: 'rgba(255, 255, 255, 0.05)',
                          },
                        }}
                      >
                        Back
                      </Button>
                    )}
                  </Box>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default CustomerLogin;
